import { type AuthProvider } from '@/features/iam/auth/AuthStore'
import type { Storage } from '@/infra/storage'
import { decodeTokenInfo, type TokenInfo } from '@/features/iam/auth/token-info'
import { jwtDecode } from 'jwt-decode'
import { MagicLinkV4Api } from '@/features/iam/api/iam-api'
import { links } from '@/utils/link'
import { NEVER } from 'rxjs'
import env from '@/infra/env'
import type { Id } from '@/infra/id'
import type { MagicLinkSession } from '@/infra/global-types'
import {
  type Destination,
  NavbarOptions,
  type ReturnOptions,
  type SessionOptions,
} from '@/features/iam/auth/SessionOptions'
import type { ReadPreselectedWorkspaceInfoFn } from '@/features/iam'
import type { GetLifecycleDelegate } from '@/features/shell/platform/types'

const sessionKey = '__session'

/**
 * Provides authentication through Magic Link (V4).
 */
export class MagicLinkTokenV4Provider implements AuthProvider {
  private _token: TokenInfo | null = null

  constructor(
    private readonly _sessionStorage: Storage,
    private readonly _getLifecycleDelegate: GetLifecycleDelegate,
    private readonly _readPreselectedWorkspaceInfo: ReadPreselectedWorkspaceInfoFn,
    private readonly _api: MagicLinkV4Api,
  ) {}

  /**
   * Fetches the token info.
   */
  async fetchTokenInfo(signal?: AbortSignal): Promise<TokenInfo | null> {
    signal?.throwIfAborted()

    return this.getToken(signal)
  }

  /**
   * Exchanges the magic link code for a token.
   */
  async callback(): Promise<string | null> {
    const session = window.__magicLinkSession

    if (session) {
      try {
        const res = await this._api.claimSession({ session_id: session.id })

        this.saveSession({
          ...session,
          refreshToken: res.refresh_token,
        })
        this._token = decodeTokenInfo(res.access_token)

        this._getLifecycleDelegate().notifyAuthenticated()

        return session.targetUrl
      } catch (err) {
        console.error('Error fetching magic link refresh token', err)
      }
    }

    // If there is no session or something goes wrong,
    // let's redirect the user to the expiration page
    // We'll do it with an explicit browser navigation to avoid
    // the loader from calling back in.
    await this.redirectToExpirationPage()
    return links.sessionExpired()
  }

  /**
   * Redirects the user to the expiration page.
   */
  redirectToLogin(): Promise<void> {
    return this.redirectToExpirationPage()
  }

  /**
   * Redirects the user to the expiration page.
   */
  redirectToSignup(): Promise<void> {
    return this.redirectToExpirationPage()
  }

  /**
   * Logouts the user clearing the session and the token.
   */
  logout(): Promise<void> {
    delete window.__magicLinkSession

    this._sessionStorage.removeItem(sessionKey)

    return Promise.resolve()
  }

  /**
   * Logs out the user and redirects them to the expiration page.
   */
  async onUnauthenticated(): Promise<void> {
    const workspace = this._readPreselectedWorkspaceInfo()
    const allowRequestingMagicLinks =
      workspace?.isMagicLinkWorkspace && workspace?.allowRequestingMagicLinks

    const url = allowRequestingMagicLinks
      ? links.requestMagicLink()
      : links.sessionExpired()

    window.location.replace(url)

    // never resolves because we are redirecting
    await new Promise(() => void 0)
  }

  /**
   * Returns the session options.
   */
  getSessionOptions(): SessionOptions {
    const magicLinkSession = this.getSession()

    const navbarOptions =
      (magicLinkSession?.navbarOptions as NavbarOptions) ??
      NavbarOptions.default

    const returnOptions =
      (magicLinkSession?.returnOptions as ReturnOptions) ?? null

    const allowedLegendIds =
      (magicLinkSession?.allowedLegendIds as Id[]) ?? null

    const destination = (magicLinkSession?.destination as Destination) ?? {
      type: 'Dashboard',
    }

    return {
      navbarOptions,
      returnOptions,
      allowedLegendIds,
      destination,
    }
  }

  /**
   *  Fetches the token from the API.
   */
  private async getToken(signal?: AbortSignal) {
    const session = this.getSession()

    if (this._token?.token) {
      const currentTimestamp = Math.floor(Date.now() / 1000)
      const decoded = jwtDecode(this._token.token)

      if (decoded.exp && decoded.exp - 30 > currentTimestamp) {
        return this._token
      }
    }

    if (!session?.refreshToken) {
      return null
    }

    const refreshToken = session.refreshToken

    try {
      const res = await this._api.refreshToken(
        { refresh_token: refreshToken },
        signal,
      )
      this._token = decodeTokenInfo(res.access_token)
      // Update the stored session with the new refresh token
      this.saveSession({
        ...session,
        refreshToken: res.refresh_token,
      })
    } catch (err) {
      console.error('Error fetching magic link token', err)
      this._token = null
    }

    return this._token
  }

  /**
   * Redirects the user to the expiration page.
   */
  private redirectToExpirationPage() {
    window.location.replace(links.sessionExpired())

    return Promise.resolve()
  }

  /**
   * Retrieves the session from the session storage.
   */
  private getSession() {
    return parseSessionFromStorage(this._sessionStorage)
  }

  /**
   * Saves the session.
   *
   * @param session
   * @private
   */
  private saveSession(session: MagicLinkSession) {
    const encoded = btoa(JSON.stringify(session))
    this._sessionStorage.setItem(sessionKey, encoded)
  }

  /**
   * Creates a {@link MagicLinkTokenV4Provider} if the magic link session is present.
   */
  static maybeMagicLinkProvider(
    sessionStorage: Storage,
    getLifecycleDelegate: GetLifecycleDelegate,
    readPreselectedWorkspaceInfo: ReadPreselectedWorkspaceInfoFn,
  ): MagicLinkTokenV4Provider | null {
    const workspace = readPreselectedWorkspaceInfo()
    const isMagicLinkWorkspace = workspace?.isMagicLinkWorkspace

    const magicLinkSession = window.__magicLinkSession
    const session = parseSessionFromStorage(sessionStorage)

    const magicLinkApi = new MagicLinkV4Api({
      token$: NEVER,
      baseURL: env.IAM_API,
    })

    return isMagicLinkWorkspace || magicLinkSession || session?.refreshToken
      ? new MagicLinkTokenV4Provider(
          sessionStorage,
          getLifecycleDelegate,
          readPreselectedWorkspaceInfo,
          magicLinkApi,
        )
      : null
  }
}

function parseSessionFromStorage(storage: Storage): MagicLinkSession | null {
  const sessionEncoded = storage.getItem(sessionKey)

  if (!sessionEncoded) {
    return null
  }

  try {
    return JSON.parse(atob(sessionEncoded)) as MagicLinkSession
  } catch (e) {
    console.error('Failed to parse magic link session from storage', e)
    storage.removeItem(sessionKey)
    return null
  }
}
